import { isArray } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FilterValue } from 'react-table';
import { useTranslation } from 'react-i18next';
import { useHttp } from 'utils/hooks';
import { MServerSelectHttpServices } from 'views/MServerSelect/MServerSelect.model';

import MFilterView from './MFilter.view';
import { FlaggedCallBack } from 'utils/hooks/use-flagged-callback';
import { IGetSimpleListResponse, IGetSimpleListResponseBase } from 'models/http/common';
import { useSelector } from 'react-redux';
import { AppState } from 'models/app-store';
import { ITableFilterOptions } from './MFilter.model';
import { BLANK_FILTER_ID, FilterMetadata } from 'views/MTable/Table.model';

type Props = {
  name?: string;
  nameKey?: string;
  displayLabel?: string;
  showChevron?: boolean;
  collection?: keyof MServerSelectHttpServices;
  fixedOptions?: ITableFilterOptions[];
  value: FilterValue;
  isTranslationKey?: boolean;
  bottomToTop?: boolean;
  searchViaButton?: boolean;
  enableBlankFilter?: boolean;
  fetch?: FlaggedCallBack<(abortController?: AbortController) => Promise<{ id: string; label: string }[] | null>>;
  onChange: (value: FilterValue, metadata?: FilterMetadata) => void;
};

const MFilter = (props: React.PropsWithChildren<Props>): JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const http = useHttp();

  const [showOptionsState, setShowOptionsState] = useState(false);
  const [contentStyleState, setContentStyleState] = useState<React.CSSProperties>({});
  const [fetchedOptionsState, setFetchedOptionsState] = useState<ITableFilterOptions[]>([]);
  const [optionsState, setOptionsState] = useState<ITableFilterOptions[]>([]);

  const { t } = useTranslation();

  const filterByEntity = useSelector((state: AppState) => state.auth.session?.view?.relatedEntity) || undefined;
  const resolvedName = props.nameKey || props.name || '';
  const shouldTranslateFilterValueLabel = !!props.nameKey || !!props.isTranslationKey;
  // Labels from collection/fetch responses are real entity names, not i18n keys — don't translate them
  const shouldUseTranslationOnItems = !props.collection && !props.fetch && shouldTranslateFilterValueLabel;
  const getFilterDisplayName = () => props.displayLabel || (shouldTranslateFilterValueLabel ? t(resolvedName) : resolvedName);
  const getBlankOption = () => ({ id: BLANK_FILTER_ID, label: `${getFilterDisplayName()}: ${t('common.blank')}`, isTranslationKey: false });

  const promptChange = (index: number) => {
    if (isArray(props.value)) {
      if (props.value.find((value: FilterValue) => value.id === optionsState[index].id)) {
        const shouldRemoveBlank =
          props.enableBlankFilter &&
          props.value.find((value: FilterValue) => value.id === BLANK_FILTER_ID) &&
          fetchedOptionsState.length > 0 &&
          fetchedOptionsState.every((option) => props.value.find((value: FilterValue) => value.id === option.id));

        props.onChange(
          props.value
            .filter((value) => value.id !== optionsState[index].id && (!shouldRemoveBlank || value.id !== BLANK_FILTER_ID))
            .map((value) => ({ id: value.id, label: value.label, isTranslationKey: value.isTranslationKey ?? shouldUseTranslationOnItems })),
          {
            filterLabel: resolvedName,
          },
        );
      } else {
        const values = [
          ...props.value.map((item) => ({ id: item.id, label: item.label, isTranslationKey: item.isTranslationKey ?? shouldUseTranslationOnItems })),
          { ...optionsState[index], isTranslationKey: shouldUseTranslationOnItems },
        ];

        if (!props.enableBlankFilter && values.length === fetchedOptionsState.length) {
          onToggleAll();

          return;
        }

        props.onChange(values, {
          filterLabel: resolvedName,
        });
      }

      return;
    }

    props.onChange([{ ...optionsState[index], isTranslationKey: shouldUseTranslationOnItems }]);
  };

  const onToggleBlank = () => {
    if (!props.enableBlankFilter) {
      return;
    }

    const blankOption = getBlankOption();

    if (!isArray(props.value)) {
      props.onChange([blankOption], {
        filterLabel: resolvedName,
      });

      return;
    }

    if (props.value.find((value) => value.id === BLANK_FILTER_ID)) {
      props.onChange(
        props.value
          .filter((value) => value.id !== BLANK_FILTER_ID)
          .map((value) => ({ id: value.id, label: value.label, isTranslationKey: value.isTranslationKey ?? shouldUseTranslationOnItems })),
        {
          filterLabel: resolvedName,
        },
      );

      return;
    }

    props.onChange(
      [
        ...props.value.map((item) => ({ id: item.id, label: item.label, isTranslationKey: item.isTranslationKey ?? shouldUseTranslationOnItems })),
        blankOption,
      ],
      {
        filterLabel: resolvedName,
      },
    );
  };

  const onEnterValue = (value: string) => {
    // Pre-compute the display label so it survives JSON serialization (functions are lost in session cache)
    const displayName = shouldTranslateFilterValueLabel ? t(resolvedName) : resolvedName;
    props.onChange(
      [
        ...(props.value?.map((item) => ({ id: item.id, label: item.label, isTranslationKey: item.isTranslationKey })) || []),
        { id: value, label: `${displayName}: ${value}`, isTranslationKey: false },
      ],
      {
        filterLabel: resolvedName,
      },
    );
  };

  useEffect(() => {
    if (showOptionsState && dropdownRef.current) {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && dropdownRef.current !== event.target && !dropdownRef.current.contains(event.target as HTMLElement)) {
          setShowOptionsState(() => false);
        }
      };

      document.addEventListener('click', handleClickOutside);

      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [showOptionsState, dropdownRef, setShowOptionsState]);

  const updateDropdownPosition = useCallback(() => {
    if (!containerRef.current) {
      return;
    }

    const rect = containerRef.current.getBoundingClientRect();
    const dropdownRect = dropdownRef.current?.getBoundingClientRect();
    const dropdownHeight = dropdownRect?.height || 0;
    const dropdownWidth = dropdownRect?.width || 0;
    const viewportPadding = 8;

    const rawTop = rect.top - 16;
    const rawLeft = rect.left + 200;
    const maxTop = window.innerHeight - dropdownHeight - viewportPadding;
    const maxLeft = window.innerWidth - dropdownWidth - viewportPadding;

    setContentStyleState({
      top: Math.max(viewportPadding, Math.min(rawTop, maxTop)),
      left: Math.max(viewportPadding, Math.min(rawLeft, maxLeft)),
    });
  }, []);

  useEffect(() => {
    if (!showOptionsState) {
      return;
    }

    updateDropdownPosition();

    window.addEventListener('resize', updateDropdownPosition);
    window.addEventListener('scroll', updateDropdownPosition, true);

    return () => {
      window.removeEventListener('resize', updateDropdownPosition);
      window.removeEventListener('scroll', updateDropdownPosition, true);
    };
  }, [showOptionsState, updateDropdownPosition]);

  useEffect(() => {
    if (!showOptionsState) {
      return;
    }

    updateDropdownPosition();
  }, [showOptionsState, optionsState.length, updateDropdownPosition]);

  const fetch = useCallback(() => {
    if (props.fixedOptions) {
      setOptionsState(() => props.fixedOptions || []);

      return;
    }

    let abortController: AbortController | null = new AbortController();

    const defaultFetch = async (abortController?: AbortController): Promise<{ id: string; label: string }[] | null> => {
      if (!props.collection) {
        return null;
      }

      return http[props.collection]
        .getSimple(null, abortController)
        .then((response: IGetSimpleListResponse) => {
          if (filterByEntity && props.collection && filterByEntity.name.includes(props.collection)) {
            return response.list.filter((item: IGetSimpleListResponseBase) => {
              return item.id == filterByEntity.numericId.toString();
            });
          }

          return response.list;
        })
        .catch(() => null);
    };

    const fetchFunc = props.fetch || defaultFetch;

    fetchFunc(abortController)
      .then((list: { id: string; label: string }[] | null) => {
        if (list) {
          setOptionsState(() => list);
          setFetchedOptionsState(() => list);
        }
      })
      .catch(() => setOptionsState(() => []))
      .finally(() => {
        abortController = null;
      });

    return () => abortController?.abort();
  }, [props.fixedOptions, setOptionsState, setFetchedOptionsState]);

  useEffect(() => {
    if (showOptionsState) {
      fetch();
    }
  }, [showOptionsState]);

  const onFilterOptions = (event: React.FormEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;

    setOptionsState(() => {
      if (props.fixedOptions) {
        return props.fixedOptions.filter((valueOptions: ITableFilterOptions) => {
          return valueOptions.label.toLowerCase().includes(value.toLowerCase());
        });
      }

      return fetchedOptionsState.filter((valueOptions: ITableFilterOptions) => {
        return valueOptions.label.toLowerCase().includes(value.toLowerCase());
      });
    });
  };

  const isChecked = (id: string): boolean => {
    if (filterByEntity && props.collection && filterByEntity.name.includes(props.collection)) {
      return true;
    }

    if (isArray(props.value)) {
      return props.value.find((value) => value.id === id);
    }

    if (typeof props.value === 'object') {
      return props.value['id'] === id;
    }

    return false;
  };

  const selectedIds = useMemo(() => {
    if (isArray(props.value)) {
      return props.value.map((value) => value.id);
    }

    if (typeof props.value === 'object' && props.value !== null && 'id' in props.value) {
      return [props.value.id];
    }

    return [];
  }, [props.value]);

  const isAllSelected =
    fetchedOptionsState.length > 0 &&
    fetchedOptionsState.every((option) => selectedIds.includes(option.id)) &&
    (!props.enableBlankFilter || selectedIds.includes(BLANK_FILTER_ID)) &&
    (!filterByEntity || !props.collection || !filterByEntity.name.includes(props.collection));

  const onToggleAll = () => {
    if (!optionsState.length) {
      return;
    }

    if (filterByEntity && props.collection && filterByEntity.name.includes(props.collection)) {
      return;
    }

    if (isAllSelected) {
      props.onChange([], {
        filterLabel: resolvedName,
      });

      return;
    }

    const selected = [
      ...optionsState.map((option) => ({ id: option.id, label: option.label, isTranslationKey: shouldUseTranslationOnItems })),
      ...(props.enableBlankFilter ? [getBlankOption()] : []),
    ];

    props.onChange(selected, {
      filterLabel: resolvedName,
      isAllSelected: true,
    });
  };

  return (
    <MFilterView
      name={resolvedName}
      displayLabel={props.displayLabel}
      showChevron={props.showChevron}
      options={optionsState}
      isOpenedFilter={showOptionsState}
      containerRef={containerRef}
      dropdownRef={dropdownRef}
      contentStyle={contentStyleState}
      value={props.value}
      showSelectAll={!filterByEntity || !props.collection || !filterByEntity.name.includes(props.collection)}
      showBlankOption={!!props.enableBlankFilter}
      isAllSelected={isAllSelected}
      isFiltered={fetchedOptionsState.length !== optionsState.length}
      onToggleAll={onToggleAll}
      onToggleBlank={onToggleBlank}
      onSelect={promptChange}
      onFilterOptions={onFilterOptions}
      onOpenFilter={() => setShowOptionsState((prev) => !prev)}
      bottomToTop={props.bottomToTop}
      searchViaButton={props.searchViaButton}
      enterValue={onEnterValue}
      fixedOptions={!props.collection && !props.fetch}
      isChecked={isChecked}
    ></MFilterView>
  );
};

MFilter.displayName = 'MFilter';

export default MFilter;
